import React, { useEffect, useRef } from 'react';
import { NavigationContainer, LinkingOptions } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import { Colors } from '@/constants/colors';
import { useAuthStore } from '@/store/authStore';
import { AuthNavigator } from './AuthNavigator';
import { MainNavigator } from './MainNavigator';
import { RootStackParamList } from './types';
import { useNotifications, setNotificationNavigationRef } from '@/hooks/useNotifications';
import { supabase } from '@services/supabase';

const Stack = createStackNavigator<RootStackParamList>();

/**
 * Root App Navigator
 * Manages navigation between authenticated and unauthenticated states
 */
export const AppNavigator: React.FC = () => {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const navigationRef = useRef<any>(null);

  // Initialize notifications (inside NavigationContainer context)
  const { isInitialized } = useNotifications();

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Set navigation ref when ready
  useEffect(() => {
    if (navigationRef.current) {
      setNotificationNavigationRef(navigationRef.current);
      console.log('✅ [AppNavigator] Navigation ref set for notifications');
    }
  }, [navigationRef.current]);

  // Log notification initialization
  useEffect(() => {
    if (isInitialized) {
      console.log('✅ [AppNavigator] Notifications initialized successfully');
    }
  }, [isInitialized]);

  // Keep auth state in sync with Supabase (helps mobile web timing)
  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, _session) => {
      // Re-check auth when session changes
      checkAuth();
    });
    return () => {
      try {
        subscription?.subscription?.unsubscribe();
      } catch {}
    };
  }, [checkAuth]);

  // Show loading screen while checking auth
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.accent.primary} />
      </View>
    );
  }

  // Deep linking configuration (web + native custom scheme)
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://app.example.com';
  const linking: LinkingOptions<RootStackParamList> = {
    prefixes: [origin, 'tickervibes://'],
    config: {
      screens: {
        Main: {
          screens: {
            Home: {
              screens: {
                HomeScreen: '',
                StockAnalysis: 'stock/:ticker',
              },
            },
            Watchlist: 'watchlist',
            Groups: 'groups',
            Feedback: 'feedback',
            Profile: 'profile',
          },
        },
        Auth: {
          path: 'auth',
          screens: {
            Callback: 'callback',
          },
        },
      },
    },
  };

  return (
    <NavigationContainer ref={navigationRef} linking={linking}>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {isAuthenticated ? (
          <Stack.Screen name="Main" component={MainNavigator} />
        ) : (
          <Stack.Screen name="Auth" component={AuthNavigator} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
  },
});
